import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useState } from 'react'
import type { AccountSummary } from '@/api/accounts'
import type { AttributedHolding, ValueReasons } from '@/domain/attribution'
import { attributeHoldings, attributedTotal, hasReasons, valueReasons } from '@/domain/attribution'
import type { Subtotal } from '@/domain/money'
import type { HoldingRow, HoldingsResponse } from '~/api/client'
import { api, apiFetch, formatMinor, formatQuantity } from '~/api/client'
import { formatShareBp } from '~/charts/format'
import { chartCss } from '~/charts/theme'
import { Card, CardHead, StatCard } from '~/components/ui/Card'
import { Button, WhyButton } from '~/components/ui/Controls'
import { Figure } from '~/components/ui/Figure'
import { Field } from '~/components/ui/Modal'
import { Monogram, StatusPill, toneOfHolding } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import { SETTINGS_PATH } from '~/navigation'
import { today } from '~/state/dates'

interface AccountsResponse {
  accounts: AccountSummary[]
}

const CSS = `
.nw-holdings .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); margin-bottom: var(--nw-stack); }
.nw-holdings .nw-stats > * { flex: 1 1 200px; }
.nw-holdings .nw-notes { margin-bottom: var(--nw-stack); }
/* The hatch used to run under the figures of every projected row, which is the
   one place on this page a digit has to be read exactly. It marks the edge of
   the row now and tints the rest. */
.nw-holdings tbody tr[data-projected='true'] td { background: color-mix(in srgb, var(--nw-muted) 9%, transparent); }
.nw-holdings tbody tr[data-projected='true'] td:first-child {
  background:
    repeating-linear-gradient(135deg, var(--nw-hairline), var(--nw-hairline) 1px, transparent 1px, transparent 5px) left / 7px 100% no-repeat,
    color-mix(in srgb, var(--nw-muted) 9%, transparent);
}
/* The mark down the edge of a projected row needs the column it is drawn in to
   start a little further in, on every row rather than on the marked ones. Asked
   for by setting the inset the system already gives a table, rather than by
   reaching past it into the cells: a page that writes its own first-child
   rule is the shape that gave a flush card's inset to the heading alone. */
.nw-holdings .nw-data { --nw-table-inset: var(--nw-space-3); --nw-table-inset-end: 0; }
.nw-holdings .nw-marks { display: flex; flex-wrap: wrap; gap: var(--nw-space-1); }
/* The dot sits after the figure without pushing it off the edge the column is
   read down. */
.nw-holdings .nw-attributed { align-items: center; display: inline-flex; gap: var(--nw-space-2); justify-content: flex-end; }
/* An explanation is a paragraph, so its row is as tall as it needs to be rather
   than the 40px every other row is held to. */
.nw-holdings .nw-why-row td { height: auto; }
/* Stuck to the left edge of whatever is on screen: the table scrolls sideways
   on a phone, and an explanation that stayed where its cell begins was written
   off the side of the screen for anyone who had scrolled to the column it
   explains. */
.nw-holdings .nw-why-note {
  color: var(--nw-muted); font-size: var(--nw-text-chip); left: 0;
  max-width: min(88ch, calc(100vw - 100px)); padding: var(--nw-space-3) 0; position: sticky;
}
.nw-holdings .nw-why-note ul { margin: 0; padding-left: var(--nw-space-4); }
.nw-holdings .nw-why-note li { line-height: 1.5; }
.nw-holdings .nw-why-note p { color: var(--nw-ink); margin: var(--nw-space-2) 0 0; }
.nw-holdings .nw-accounts .nw-note { margin: var(--nw-space-2) 0 0; }
.nw-holdings .nw-retire-from { margin: var(--nw-space-4) 0; }
.nw-holdings .nw-retire-from .nw-field { width: 180px; }
.nw-holdings .nw-row-action { display: flex; justify-content: flex-end; }
`

/*
 * The column and the figures above it were the same word for two different
 * amounts, and only a holding that is part-owned or priced abroad told them
 * apart — which is exactly where being wrong costs something.
 */
const LEDE =
  'Value is your household’s share of each position: what it puts into Look-through, not what ' +
  'the whole position is worth. A row that comes to something other than quantity × price says why.'

const POSITION_COLUMNS = [
  { id: 'holding', label: 'Holding' },
  { id: 'account', label: 'Account', width: '170px' },
  { id: 'type', label: 'Type', width: '90px' },
  { id: 'quantity', label: 'Quantity', width: '110px', align: 'right' as const },
  { id: 'price', label: 'Price', width: '140px', align: 'right' as const },
  { id: 'value', label: 'Value', width: '150px', align: 'right' as const },
  { id: 'flags', label: 'Flags', width: '170px' },
]

const ACCOUNT_COLUMNS = [
  { id: 'name', label: 'Account' },
  { id: 'entity', label: 'Entity', width: '150px' },
  { id: 'source', label: 'Source', width: '140px' },
  { id: 'reported', label: 'Last reported', width: '140px' },
  { id: 'status', label: 'Status', width: '170px' },
  { id: 'action', label: ' ', width: '120px' },
]

export function Holdings(): ReactElement {
  const { data, error, isPending } = useQuery({
    queryKey: ['holdings'],
    queryFn: () => api.holdings(),
  })
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => apiFetch<AccountsResponse>('/accounts'),
  })

  return (
    <main className="nw nw-holdings">
      <style>{chartCss + CSS}</style>
      <h1>Holdings</h1>
      {/*
        Counts and scope rather than a sentence about the word "Holdings". The
        title already says what the page is; the line under it is for what a
        reader cannot see without counting — how many positions, from how many
        connections, and what currency every figure below is in.
      */}
      {data && <p className="nw-sub">{scopeOf(data)}</p>}
      {data && <Totals data={data} accounts={accounts.data?.accounts ?? []} />}

      <div className="nw-stack">
        <Card>
          <CardHead
            title="Positions"
            lede={LEDE}
            aside={
              <>
                  {data && (
                    <span>
                      {data.date ? `As of ${data.date}` : 'No snapshot has been taken yet'}
                      {data.flags.oldestPriceDate && data.flags.oldestPriceDate !== data.date
                        ? ` · oldest price used is from ${data.flags.oldestPriceDate}`
                        : ''}
                    </span>
                  )}
                  {/*
                    Hand entry has no destination of its own any more, and what is
                    already entered still has to be editable, valued and taken back
                    out. It sits where the mockup keeps a table's controls — at the
                    far end of that table's own title — rather than under the page
                    heading, where it read as a statement about the whole page.

                    Outside everything the figures depend on, because a page that
                    could not load them is exactly when somebody goes looking for
                    what they typed in themselves.
                  */}
                  <a href="/manual">Change or remove what was entered by hand</a>
              </>
            }
          />
          {isPending && <p className="nw-empty">Loading…</p>}
          {error && (
            <p className="nw-flag nw-flag--stale">Could not load holdings: {error.message}</p>
          )}
          {data && <Positions data={data} />}
        </Card>

        {/* Held back until the snapshot is known: the date a retirement runs
            from defaults to the day the page is showing, and a card mounted
            before that would default to today and keep it. */}
        {data && <Accounts accounts={accounts.data?.accounts ?? []} date={data.date} />}
      </div>
    </main>
  )
}

/**
 * Every position, ordered by what it puts into the totals, under the figure
 * they come to.
 */
function Positions({ data }: { data: HoldingsResponse }): ReactElement {
  const attributed = attributeHoldings(data.rows)
  const ordered = [...attributed].sort(
    (a, b) =>
      (b.attributedMinor ?? 0) - (a.attributedMinor ?? 0) ||
      a.holding.accountName.localeCompare(b.holding.accountName),
  )

  return (
    <DataTable
      columns={POSITION_COLUMNS}
      label={`Positions, in ${data.baseCurrency}`}
      total={
        data.rows.length === 0 ? undefined : (
          <LookThrough data={data} subtotal={attributedTotal(attributed)} />
        )
      }
    >
      {ordered.map((holding) => (
        <Row
          key={`${holding.holding.accountId}-${holding.holding.instrumentId}`}
          attributed={holding}
          base={data.baseCurrency}
          date={data.date}
        />
      ))}
      {data.rows.length === 0 && (
        <tr>
          <td colSpan={POSITION_COLUMNS.length}>No positions recorded for this date.</td>
        </tr>
      )}
    </DataTable>
  )
}

/**
 * What the column adds up to, named as the figure it is.
 *
 * This row was left unbuilt while the column was the whole value of each
 * position: summing those would have come to a net worth nobody has, and it
 * would have looked right, because every share in the seeded data is a hundred
 * per cent. The column is the household's share now, so the sum is Look-through
 * — and where it is not, the row says so rather than presenting a figure that
 * is nearly the one above it.
 */
function LookThrough({
  data,
  subtotal,
}: {
  data: HoldingsResponse
  subtotal: Subtotal
}): ReactElement {
  const { baseCurrency } = data
  const totalMinor = subtotal.valueMinor
  const differenceMinor = totalMinor === null ? 0 : totalMinor - data.totals.lookThroughMinor

  return (
    <tr>
      <td>Look-through</td>
      <td colSpan={4} className="nw-muted">
        {totalMinor === null
          ? 'No position above carries a price, so there is nothing here to total.'
          : `${
              differenceMinor === 0
                ? 'Every row above at your share of it, which is the Look-through figure at the top of this page.'
                : `This is ${formatMinor(Math.abs(differenceMinor), baseCurrency)} ${
                    differenceMinor > 0 ? 'more' : 'less'
                  } than the Look-through figure at the top of this page. One of the two is wrong.`
            }${
              subtotal.absent > 0
                ? subtotal.absent === 1
                  ? ' One position above carries no price and is in neither figure.'
                  : ` ${subtotal.absent} positions above carry no price and are in neither figure.`
                : ''
            }`}
      </td>
      <td className="nw-num">
        {totalMinor === null ? (
          <span className="nw-missing">no total</span>
        ) : (
          <Figure amountMinor={totalMinor} currency={baseCurrency} size="body" />
        )}
      </td>
      <td>
        {totalMinor !== null && differenceMinor !== 0 && (
          <span className="nw-marks">
            <StatusPill tone="failed" label="Does not reconcile" />
          </span>
        )}
      </td>
    </tr>
  )
}

/** The three figures the page is about, and every qualification they carry. */
function Totals({
  data,
  accounts,
}: {
  data: HoldingsResponse
  accounts: readonly AccountSummary[]
}): ReactElement {
  const { baseCurrency, totals } = data
  // Without a second entity holding something, Spendable is the look-through
  // figure to the øre: a third card repeating the first, explained by a company
  // the reader does not have. Read from the same payload as the figures rather
  // than from a request of its own, so the row of cards is never drawn twice.
  const holdsCompany = data.flags.holdsThroughCompany
  // And Liquid, where nothing held cannot be sold: it subtracts nothing from the
  // card before it, so it would be that card again (PRO-267).
  const holdsUnsellable = data.flags.holdsUnsellable
  // The snapshot leaves an unpriced holding out of its totals, so a household
  // whose every position is unpriced gets three honest zeros that read as a net
  // worth of nothing. Nothing has been measured, so nothing is stated.
  const nothingPriced =
    data.rows.length > 0 && data.rows.every((row) => row.valueMinor === null)
  const staleSources = data.sources.filter((s) => s.status === 'carried_forward')
  const retired = accounts.flatMap((a) =>
    a.retiredOn !== null && (data.date === null || a.retiredOn <= data.date)
      ? [{ id: a.id, name: a.name, retiredOn: a.retiredOn }]
      : [],
  )

  return (
    <>
      <div className="nw-stats">
        <StatCard
          label="Look-through"
          help="Everything you own, attributed to you by share."
        >
          {nothingPriced ? (
            <span className="nw-missing">—</span>
          ) : (
            <Figure amountMinor={totals.lookThroughMinor} currency={baseCurrency} size="figure" />
          )}
        </StatCard>
        {holdsCompany && (
          <StatCard
            label="Spendable"
            help="What is left after the tax on getting a company's money out."
          >
            {nothingPriced ? (
              <span className="nw-missing">—</span>
            ) : (
              <Figure amountMinor={totals.spendableMinor} currency={baseCurrency} size="figure" />
            )}
          </StatCard>
        )}
        {holdsUnsellable && (
          <StatCard label="Liquid" help="The part that could be sold without asking anyone.">
            {nothingPriced ? (
              <span className="nw-missing">—</span>
            ) : (
              <Figure amountMinor={totals.liquidMinor} currency={baseCurrency} size="figure" />
            )}
          </StatCard>
        )}
      </div>

      <div className="nw-notes">
        {!holdsCompany ? null : data.flags.taxRatesConfigured === false ? (
          <p className="nw-flag nw-flag--missing">
            <span className="nw-flag-icon" aria-hidden="true">
              ✕
            </span>
            <span>
              <b>No tax rates for this date</b> — nothing has been subtracted for getting the
              company's money out, so Spendable is the figure before tax rather than the honest
              one. <a href={SETTINGS_PATH}>Set the rates in Settings</a>, from a date at or before
              this one.
            </span>
          </p>
        ) : (
          <p className="nw-note">
            Spendable subtracts an estimated {formatMinor(totals.taxMinor, baseCurrency)} of
            extraction tax. It is an estimate for orientation, not tax advice.
          </p>
        )}

        {totals.projectedMinor !== 0 && (
          <p className="nw-note">
            {formatMinor(totals.projectedMinor, baseCurrency)} of the figures above rests on
            projected positions.
          </p>
        )}

        {staleSources.length > 0 && (
          <p className="nw-flag nw-flag--stale">
            {staleSources
              .map((s) => `${s.source} has not reported since ${s.staleSince ?? 'an unknown date'}`)
              .join('; ')}
            . Those positions are carried forward, not confirmed.
          </p>
        )}

        {(data.missingPrices.length > 0 || data.missingFx.length > 0) && (
          <p className="nw-flag nw-flag--stale">
            Missing {data.missingPrices.length > 0 && `prices for ${data.missingPrices.join(', ')}`}
            {data.missingPrices.length > 0 && data.missingFx.length > 0 && ' and '}
            {data.missingFx.length > 0 && `FX rates for ${data.missingFx.join(', ')}`}. Those
            holdings are left out of the totals rather than counted as zero.
          </p>
        )}

        {data.carriedFx.length > 0 && (
          <p className="nw-flag nw-flag--stale">
            {data.carriedFx.join(', ')} {data.carriedFx.length === 1 ? 'is' : 'are'} converted at
            the last rate the ECB published, not one confirmed for today. The figures above are
            real; the rate behind them is carried forward.
          </p>
        )}

        {retired.length > 0 && (
          <p className="nw-note">
            Not shown: {retired.map((a) => `${a.name}, retired ${a.retiredOn}`).join('; ')}. Their
            history is untouched.
          </p>
        )}
      </div>
    </>
  )
}

/**
 * Nothing else can stop an account contributing: a closed source never reports
 * the empty holding that would end its carry-forward.
 */
function Accounts({
  accounts,
  date,
}: {
  accounts: readonly AccountSummary[]
  date: string | null
}): ReactElement | null {
  const client = useQueryClient()
  const [from, setFrom] = useState(date ?? today())
  const [problem, setProblem] = useState<string | null>(null)

  const invalidate = async (): Promise<void> => {
    setProblem(null)
    await client.invalidateQueries({ queryKey: ['accounts'] })
    await client.invalidateQueries({ queryKey: ['holdings'] })
  }

  const retire = useMutation({
    mutationFn: (id: number) =>
      apiFetch<AccountSummary>(`/accounts/${id}/retirement`, {
        method: 'PUT',
        body: JSON.stringify({ date: from }),
      }),
    onSuccess: invalidate,
    onError: (e: Error) => setProblem(e.message),
  })

  const unretire = useMutation({
    mutationFn: (id: number) =>
      apiFetch<AccountSummary>(`/accounts/${id}/retirement`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (e: Error) => setProblem(e.message),
  })

  if (accounts.length === 0) return null

  return (
    <Card className="nw-accounts">
      <CardHead title="Accounts" />
      <p className="nw-note">
        Retiring an account stops it counting from the date given. Every date before that keeps the
        value it had.
      </p>
      {problem && <p className="nw-flag nw-flag--stale">{problem}</p>}

      <div className="nw-retire-from">
        <Field id="nw-retire-from" label="Retire from">
          <input
            id="nw-retire-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
      </div>

      <DataTable columns={ACCOUNT_COLUMNS} label="Accounts">
        {accounts.map((account) => (
          <tr key={account.id}>
            <td>{account.name}</td>
            <td>{account.entityName}</td>
            <td>{account.source}</td>
            <td>{account.lastPositionDate ?? <span className="nw-missing">never</span>}</td>
            <td>
              {account.retiredOn === null ? (
                <StatusPill tone="live" label="Live" />
              ) : (
                <StatusPill tone="failed" label={`Retired ${account.retiredOn}`} />
              )}
            </td>
            <td>
              <span className="nw-row-action">
                {account.retiredOn === null ? (
                  <Button disabled={retire.isPending} onClick={() => retire.mutate(account.id)}>
                    Retire
                  </Button>
                ) : (
                  <Button disabled={unretire.isPending} onClick={() => unretire.mutate(account.id)}>
                    Un-retire
                  </Button>
                )}
              </span>
            </td>
          </tr>
        ))}
      </DataTable>
    </Card>
  )
}

/**
 * How many positions, from how many connections, in what currency.
 *
 * Connections are counted off the rows rather than off every source on file: a
 * source that reported nothing today is not somewhere these figures came from.
 */
function scopeOf(data: HoldingsResponse): string {
  const positions = data.rows.length
  const connections = new Set(data.rows.map((row) => row.source)).size
  const unpriced = data.rows.filter((row) => row.valueMinor === null).length
  return (
    `${positions} position${positions === 1 ? '' : 's'} ` +
    `across ${connections} connection${connections === 1 ? '' : 's'} ` +
    (unpriced > 0 ? `· ${unpriced} without a price, in no figure here ` : '') +
    `· all figures in ${data.baseCurrency}`
  )
}

/** The first two letters of the ticker, which is what the tile has room for. */
function initialsOf(symbol: string): string {
  return symbol.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || '··'
}

const KIND_LABELS: Readonly<Record<string, string>> = {
  cash: 'Cash',
  equity: 'Equity',
  etf: 'ETF',
  fund: 'Fund',
  crypto: 'Crypto',
  private: 'Private',
}

function Row({
  attributed,
  base,
  date,
}: {
  attributed: AttributedHolding<HoldingRow>
  base: string
  date: string | null
}): ReactElement {
  const { holding: row, attributedMinor } = attributed
  const [open, setOpen] = useState(false)
  const reasons = valueReasons(row, base)
  const explained = attributedMinor !== null && hasReasons(reasons)
  const noteId = `nw-why-${row.accountId}-${row.instrumentId}`
  const projected = !row.confirmed || row.stale

  return (
    <>
      <tr data-projected={projected}>
        <td>
          <span className="nw-measure">
            <Monogram
              initials={initialsOf(row.symbol)}
              tone={toneOfHolding(row.instrumentKind, row.valueMinor)}
              size="sm"
            />
            <span className="nw-source-name">
              <span>{row.symbol}</span>
              <small>{row.instrumentName}</small>
            </span>
          </span>
        </td>
        <td>
          <span className="nw-source-name">
            <span>{row.accountName}</span>
            <small>{row.entityName}</small>
          </span>
        </td>
        <td className="nw-muted">{KIND_LABELS[row.instrumentKind] ?? row.instrumentKind}</td>
        <td className="nw-num">{formatQuantity(row.quantity)}</td>
        <td className="nw-num">
          {row.priceMinor === null || row.priceCurrency === null ? (
            <span className="nw-missing">no price</span>
          ) : (
            <Figure
              amountMinor={row.priceMinor}
              currency={row.priceCurrency}
              options={{ maximumFractionDigits: 2 }}
              size="cell"
              title={
                row.priceFromSharedFeed
                  ? `Priced ${row.priceDate ?? 'unknown'} from the shared feed — another household's provider, not a source of yours`
                  : `Priced ${row.priceDate ?? 'unknown'}`
              }
            />
          )}
        </td>
        <td className="nw-num">
          {attributedMinor === null ? (
            <span className="nw-missing">—</span>
          ) : (
            <span className="nw-attributed">
              <Figure amountMinor={attributedMinor} currency={base} size="cell" />
              {explained && (
                <WhyButton
                  label={`Why ${row.symbol} does not count as quantity times price`}
                  expanded={open}
                  controls={noteId}
                  onToggle={() => setOpen((was) => !was)}
                />
              )}
            </span>
          )}
        </td>
        <td>
          <span className="nw-marks">
            {!row.confirmed && <StatusPill tone="caution" label="Projected" />}
            {row.stale && (
              <StatusPill
                tone="failed"
                label={`Stale${row.staleSince ? ` since ${row.staleSince}` : ''}`}
              />
            )}
            {!row.liquid && <StatusPill tone="manual" label="Illiquid" />}
            {/* Somebody else's provider priced this, not a source of the
                household's own — a different claim, which it cannot otherwise
                tell from a price its broker gave. */}
            {row.priceFromSharedFeed && <StatusPill tone="manual" label="Shared price" />}
          </span>
        </td>
      </tr>
      {open && (
        <tr id={noteId} className="nw-why-row" data-projected={projected}>
          <td colSpan={POSITION_COLUMNS.length}>
            <WhyNote
              row={row}
              reasons={reasons}
              attributedMinor={attributedMinor}
              base={base}
              date={date}
            />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * What a row contributes, and every reason it is not the quantity times the
 * price beside it.
 *
 * A row of the table rather than a panel over it: the table scrolls sideways on
 * a phone, and anything floating above a column in it is clipped by the scroll
 * or lands off the screen. Each sentence is drawn from the row itself, and a
 * mechanism that did not touch this holding is not mentioned at all.
 */
function WhyNote({
  row,
  reasons,
  attributedMinor,
  base,
  date,
}: {
  row: HoldingRow
  reasons: ValueReasons
  attributedMinor: number | null
  base: string
  date: string | null
}): ReactElement {
  const share = reasons.shareBp === null ? null : formatShareBp(reasons.shareBp)

  return (
    <div className="nw-why-note">
      <ul>
        {reasons.priceCurrency !== null && (
          <li>
            Priced in {reasons.priceCurrency}, converted to {base} at the rate for{' '}
            {date ?? 'the day it was valued'}.
          </li>
        )}
        {reasons.haircutBp !== null && (
          <li>
            A {formatShareBp(reasons.haircutBp)} haircut on the valuation is taken off before it
            counts.
          </li>
        )}
        {reasons.discountedFurther && (
          <li>
            A further discount is taken off as well — the one set for private holdings in Settings,
            which the household sets and this holding does not carry.
          </li>
        )}
        {share !== null && (
          <li>
            {share} of {row.accountName} is yours, so {share} of the position counts.
          </li>
        )}
      </ul>
      {share !== null && attributedMinor !== null && row.valueMinor !== null && (
        <p>
          Worth <Figure amountMinor={row.valueMinor} currency={base} size="cell" /> in full, of which{' '}
          <Figure amountMinor={attributedMinor} currency={base} size="cell" /> is counted here.
        </p>
      )}
    </div>
  )
}
